import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  productsService,
  Product,
  CreateProductData,
  UpdateProductData,
} from '../services/firebaseFunctions';
import { operationErrorMessage } from '../utils/adminFeedback';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

// ---------------------------------------------------------------------------
// Validação client-side (defesa em profundidade — backend é a autoridade)
// ---------------------------------------------------------------------------
function validateProductForm(data: { titulo: string }): string | null {
  if (!data.titulo.trim()) return 'Título é obrigatório.';
  return null;
}

// ---------------------------------------------------------------------------
// Estado inicial do formulário
// ---------------------------------------------------------------------------
const EMPTY_FORM: Omit<CreateProductData, 'isActive'> & { isActive: boolean } = {
  tipo: 'ebook',
  titulo: '',
  isActive: true,
};

const ProductsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('ativos', 'editar');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [productPendingDeletion, setProductPendingDeletion] = useState<Product | null>(null);

  // ---------------------------------------------------------------------------
  // Carregamento de dados
  // ---------------------------------------------------------------------------
  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsService.getAll();
      setProducts(data);
    } catch {
      setSnackbar({
        open: true,
        message: 'Não foi possível carregar os Ativos.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // ---------------------------------------------------------------------------
  // Diálogo criar / editar
  // ---------------------------------------------------------------------------
  const handleOpenDialog = (product?: Product) => {
    setFormError(null);
    if (product) {
      setEditingProduct(product);
      setFormData({
        tipo: product.tipo,
        titulo: product.titulo,
        isActive: product.isActive,
      });
    } else {
      setEditingProduct(null);
      setFormData({ ...EMPTY_FORM });
    }
    setOpenDialog(true);
  };

  const resetDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setFormError(null);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    resetDialog();
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    const validationError = validateProductForm(formData);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);

    try {
      setSubmitting(true);
      if (editingProduct) {
        const payload: UpdateProductData = {
          tipo: formData.tipo,
          titulo: formData.titulo.trim(),
          isActive: formData.isActive,
        };
        const result = await productsService.update(editingProduct.id, payload);
        if (result.success) {
          await loadProducts();
          setSnackbar({ open: true, message: 'Ativo atualizado com sucesso!', severity: 'success' });
          resetDialog();
        } else {
          setSnackbar({ open: true, message: operationErrorMessage('product', result), severity: 'error' });
        }
      } else {
        const payload: CreateProductData = {
          tipo: formData.tipo,
          titulo: formData.titulo.trim(),
          isActive: formData.isActive,
        };
        const result = await productsService.create(payload);
        if (result.success) {
          await loadProducts();
          setSnackbar({ open: true, message: 'Ativo criado com sucesso!', severity: 'success' });
          resetDialog();
        } else {
          setSnackbar({ open: true, message: operationErrorMessage('product', result), severity: 'error' });
        }
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('product'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Deleção
  // ---------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!productPendingDeletion) return;
    try {
      setDeletingProductId(productPendingDeletion.id);
      const result = await productsService.delete(productPendingDeletion.id);
      if (result.success) {
        setProducts((prev) => prev.filter((p) => p.id !== productPendingDeletion.id));
        setSnackbar({ open: true, message: 'Ativo excluído com sucesso!', severity: 'success' });
        setProductPendingDeletion(null);
      } else {
        setSnackbar({ open: true, message: operationErrorMessage('product', result), severity: 'error' });
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('product'),
        severity: 'error',
      });
    } finally {
      setDeletingProductId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Renderização
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Ativos
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Ativos.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!canManage}>
              Novo ativo
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => {
              const rowBusy = deletingProductId === product.id;
              return (
                <TableRow
                  key={product.id}
                  hover
                  sx={{ opacity: product.isActive ? 1 : 0.55 }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {product.titulo}
                      {!product.isActive && (
                        <Chip label="Inativo" size="small" color="default" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.tipo === 'ebook' ? 'Ebook' : 'Audiobook'}
                      size="small"
                      color={product.tipo === 'ebook' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.isActive ? 'Ativo' : 'Inativo'}
                      color={product.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={canManage ? 'Editar' : 'Você não tem permissão de edição no módulo Ativos.'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(product)}
                          color="primary"
                          disabled={!canManage || rowBusy}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? 'Excluir' : 'Você não tem permissão de edição no módulo Ativos.'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setProductPendingDeletion(product)}
                          color="error"
                          disabled={!canManage || rowBusy}
                        >
                          {rowBusy ? <CircularProgress size={18} /> : <DeleteIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum ativo cadastrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo criar / editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProduct ? 'Editar ativo' : 'Novo ativo'}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" disabled={submitting}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'ebook' | 'audiobook' })}
                    label="Tipo"
                  >
                    <MenuItem value="ebook">Ebook</MenuItem>
                    <MenuItem value="audiobook">Audiobook</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  margin="normal"
                  required
                  disabled={submitting}
                  helperText="Texto livre — diferencia variantes (ex.: light / premium)"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label="Ativo"
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>

            {formError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting
              ? editingProduct
                ? 'Salvando…'
                : 'Criando…'
              : editingProduct
              ? 'Atualizar'
              : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DestructiveActionDialog
        open={Boolean(productPendingDeletion)}
        entityLabel="Ativo"
        entityName={productPendingDeletion?.titulo || ''}
        busy={Boolean(productPendingDeletion && deletingProductId === productPendingDeletion.id)}
        onCancel={() => setProductPendingDeletion(null)}
        onConfirm={() => void handleDelete()}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsPage;
